// WHAT: Reusable step card component for Miracle Flow session workflow
// WHY:  Provides unified interface for all session steps (meditation, affirmations, exercise, etc.) with timer and validation
// DOMAIN: Productivity → Miracle Flow → Session Management
// DEPENDENCIES: React forwardRef, step-specific components, timer logic, validation system
// RISKS:
//   - NOTE: useImperativeHandle exposes internal validation - may break if step structure changes
//   - NOTE: Step data state management is complex - may lose data on unmount
//   - WARN: YouTube embed URL parsing uses regex - may fail for non-standard URLs
//   - NOTE: Timer state is managed locally - may not sync with parent session state

import {
  CheckCircle,
  SkipForward,
  Clock,
  Target,
  BookOpen,
  PenTool,
  Brain,
  Heart,
  Zap,
} from "lucide-react";
import { useState, forwardRef, useImperativeHandle } from "react";

import { Card, Button, Stack, Heading, Badge } from "@/components/ui";
import { cn } from "@/shared/utils";

import { type FlowStep } from "../../types";
import { STEP_NAMES, STEP_DESCRIPTIONS, DEFAULT_TIMER_DURATIONS } from "../../utils/constants";
import {
  MeditationStep,
  AffirmationsStep,
  VisualizationStep,
  ExerciseStep,
  ReadingStep,
  ScribingStep,
  EveningMeditationStep,
  EveningAffirmationsStep,
  EveningReflectionStep,
} from "./steps";

export interface StepCardProperties {
  className?: string;
  customDuration?: number;
  exerciseVideoUrl?: null | string;
  isLoading?: boolean;
  onComplete?: () => void;
  onSkip?: () => void;
  onStart?: () => void;
  onTimerComplete?: () => void;
  status: "active" | "completed" | "pending";
  step: FlowStep;
}

export interface StepCardReference {
  saveCurrentStepData: () => Promise<void>;
  validateStep: () => boolean;
}

export const StepCard = forwardRef<StepCardReference, StepCardProperties>(
  (
    {
      className,
      customDuration,
      exerciseVideoUrl,
      isLoading = false,
      onComplete,
      onSkip,
      onStart,
      onTimerComplete,
      status,
      step,
    },
    reference,
  ) => {
    const [timerDuration] = useState(customDuration ?? DEFAULT_TIMER_DURATIONS[step]);
    const [isTimerActive, setIsTimerActive] = useState(false);
    interface EveningReflectionData {
      question1?: string;
      question2?: string;
      question3?: string;
    }

    interface StepData {
      eveningReflection?: EveningReflectionData;
      readingNotes?: string;
      scribing?: string;
      visualization?: string;
    }

    const [stepData, setStepData] = useState<StepData>({});
    const [selectedAffirmations, setSelectedAffirmations] = useState<Set<string>>(new Set());

    const handleTimerStart = () => {
      setIsTimerActive(true);
      onStart?.();
    };

    const handleTimerComplete = () => {
      setIsTimerActive(false);
      onTimerComplete?.();
    };

    const handleComplete = () => {
      onComplete?.();
    };

    const handleSkip = () => {
      onSkip?.();
    };

    /**
     * WHAT:
     *   Returns badge variant based on step status.
     *
     * WHY:
     *   Provides consistent badge styling for different step statuses.
     *
     * @param stepStatus - The current status of the step
     *
     * @returns Badge variant name
     *
     */
    const getBadgeVariant = (stepStatus: typeof status): "default" | "info" | "success" => {
      if (stepStatus === "completed") {
        return "success";
      }
      if (stepStatus === "active") {
        return "info";
      }
      return "default";
    };

    /**
     * WHAT:
     *   Returns badge label text based on step status.
     *
     * WHY:
     *   Provides consistent badge text for different step statuses.
     *
     * @param stepStatus - The current status of the step
     *
     * @returns Badge label text
     *
     */
    const getBadgeLabel = (stepStatus: typeof status): string => {
      if (stepStatus === "completed") {
        return "Completed";
      }
      if (stepStatus === "active") {
        return "Active";
      }
      return "Pending";
    };

    /**
     * WHAT:
     *   Toggles affirmation selection state in local Set.
     *
     * WHY:
     *   Manages local selection state before passing to parent component.
     *
     * INPUTS:
     *   - affirmationId: string — ID of affirmation to toggle
     *
     * SIDE EFFECTS:
     *   - Updates selectedAffirmations state
     *
     * RISKS:
     *   - NOTE: State is local - may not sync with parent if component unmounts
     *
     * @param affirmationId - ID of affirmation to toggle
     *
     */
    const handleAffirmationToggle = (affirmationId: string) => {
      setSelectedAffirmations((previous) => {
        const newSet = new Set(previous);
        if (newSet.has(affirmationId)) {
          newSet.delete(affirmationId);
        } else {
          newSet.add(affirmationId);
        }
        return newSet;
      });
    };

    /**
     * WHAT:
     *   Returns appropriate icon component based on step type.
     *
     * WHY:
     *   Provides visual representation for each step type in the UI.
     *
     * OUTPUT:
     *   React icon component (Lucide icon).
     *
     * RISKS:
     *   - NOTE: Default case returns Clock icon - may not be appropriate for all unknown steps
     *
     * @returns {JSX.Element} The icon component for the step

    */
    const getStepIcon = () => {
      switch (step) {
        case "Meditation":
        case "EveningMeditation": {
          return <Brain className="w-6 h-6" />;
        }
        case "Affirmations":
        case "EveningAffirmations": {
          return <Heart className="w-6 h-6" />;
        }
        case "Visualization": {
          return <Target className="w-6 h-6" />;
        }
        case "Exercise": {
          return <Zap className="w-6 h-6" />;
        }
        case "Reading": {
          return <BookOpen className="w-6 h-6" />;
        }
        case "Scribing": {
          return <PenTool className="w-6 h-6" />;
        }
        default: {
          return <Clock className="w-6 h-6" />;
        }
      }
    };

    /**
     * WHAT:
     *   Returns Tailwind gradient classes based on step type.
     *
     * WHY:
     *   Provides consistent color coding for different step types.
     *
     * OUTPUT:
     *   Tailwind gradient class string (e.g., "from-color1 to-color2").
     *
     * RISKS:
     *   - NOTE: Color scheme is hardcoded - may not match design system updates
     *
     * @returns Tailwind gradient class string
     *
     */
    const getStepColor = () => {
      switch (step) {
        case "Meditation": {
          return "from-tracq-deep-blue to-tracq-bright-blue";
        }
        case "EveningMeditation": {
          return "from-tracq-deep-orange to-tracq-light-orange";
        }
        case "Affirmations": {
          return "from-tracq-deep-blue to-tracq-bright-blue";
        }
        case "EveningAffirmations": {
          return "from-tracq-deep-orange to-tracq-light-orange";
        }
        case "Visualization": {
          return "from-purple-500 to-purple-600";
        }
        case "Exercise": {
          return "from-green-500 to-green-600";
        }
        case "Reading": {
          return "from-orange-500 to-orange-600";
        }
        case "Scribing": {
          return "from-indigo-500 to-indigo-600";
        }
        case "EveningReflection": {
          return "from-tracq-deep-orange to-tracq-light-orange";
        }
        default: {
          return "from-[#334155] to-[#475569]";
        }
      }
    };

    /**
     * WHAT:
     *   Exposes imperative methods to parent component via ref.
     *
     * WHY:
     *   Allows parent to validate and save step data without direct state access.
     *
     * OUTPUT:
     *   Object with validateStep and saveCurrentStepData methods.
     *
     * RISKS:
     *   - WARN: validateStep always returns true - no actual validation implemented
     *   - WARN: saveCurrentStepData is a stub - does not actually save data
     *   - NOTE: Imperative handle pattern may be replaced with better state management
     */
    useImperativeHandle(reference, () => ({
      saveCurrentStepData: () => {
        // NOTE: Step data saving logic to be implemented
        // Currently step data is managed in local state and not persisted
        return Promise.resolve();
      },
      validateStep: () => {
        // NOTE: Validation logic to be implemented based on step type requirements
        // Currently returns true for all steps
        return true;
      },
    }));

    /**
     * WHAT:
     *   Renders appropriate step content component based on step type.
     *
     * WHY:
     *   Centralizes step content rendering logic with proper component selection.
     *
     * OUTPUT:
     *   React component for the current step type.
     *
     * RISKS:
     *   - NOTE: Default case shows placeholder - may not be appropriate for all unknown steps
     *   - NOTE: Step data state management is complex - may lose data on step change
     *
     * @returns The step content component
     *
     */
    const renderStepContent = () => {
      switch (step) {
        case "Meditation": {
          return (
            <MeditationStep
              duration={timerDuration}
              isActive={isTimerActive}
              onComplete={handleTimerComplete}
              onStart={handleTimerStart}
              step={step}
            />
          );
        }

        case "EveningMeditation": {
          return (
            <EveningMeditationStep
              duration={timerDuration}
              isActive={isTimerActive}
              onComplete={handleTimerComplete}
              onStart={handleTimerStart}
            />
          );
        }

        case "Affirmations": {
          return (
            <AffirmationsStep
              onAffirmationToggle={handleAffirmationToggle}
              selectedAffirmations={selectedAffirmations}
              step={step}
            />
          );
        }

        case "EveningAffirmations": {
          return (
            <EveningAffirmationsStep
              onAffirmationToggle={handleAffirmationToggle}
              selectedAffirmations={selectedAffirmations}
            />
          );
        }

        case "Visualization": {
          return (
            <VisualizationStep
              onChange={(value) => {
                setStepData({ ...stepData, visualization: value });
              }}
              value={stepData.visualization ?? ""}
            />
          );
        }

        case "Exercise": {
          return (
            <ExerciseStep
              duration={timerDuration}
              exerciseVideoUrl={exerciseVideoUrl ?? null}
              isActive={isTimerActive}
              onComplete={handleTimerComplete}
              onStart={handleTimerStart}
            />
          );
        }

        case "Reading": {
          return (
            <ReadingStep
              onChange={(value) => {
                setStepData({ ...stepData, readingNotes: value });
              }}
              value={stepData.readingNotes ?? ""}
            />
          );
        }

        case "Scribing": {
          return (
            <ScribingStep
              onChange={(value) => {
                setStepData({ ...stepData, scribing: value });
              }}
              value={stepData.scribing ?? ""}
            />
          );
        }

        case "EveningReflection": {
          // NOTE: EveningReflection uses nested state structure for three separate questions
          // WARN: Complex state updates - must spread existing eveningReflection object to preserve other questions
          return (
            <EveningReflectionStep
              // NOTE: Each question change callback must preserve other questions in nested object
              onQuestion1Change={(value) => {
                setStepData({
                  ...stepData,
                  eveningReflection: {
                    ...stepData.eveningReflection,
                    question1: value,
                  },
                });
              }}
              onQuestion2Change={(value) => {
                setStepData({
                  ...stepData,
                  eveningReflection: {
                    ...stepData.eveningReflection,
                    question2: value,
                  },
                });
              }}
              onQuestion3Change={(value) => {
                setStepData({
                  ...stepData,
                  eveningReflection: {
                    ...stepData.eveningReflection,
                    question3: value,
                  },
                });
              }}
              question1={stepData.eveningReflection?.question1 ?? ""} // NOTE: Default to empty string if undefined
              question2={stepData.eveningReflection?.question2 ?? ""}
              question3={stepData.eveningReflection?.question3 ?? ""}
            />
          );
        }

        default: {
          return (
            <Stack align="center" className="py-8" direction="col" gap={4}>
              <Clock className="w-12 h-12 text-[#94A3B8]" />
              <p className="text-[#CBD5E1] text-center">
                Complete this step to continue with your session.
              </p>
            </Stack>
          );
        }
      }
    };

    return (
      <Card
        className={cn("bg-[#1E293B] border-[#334155] w-full", className)}
        padding="lg"
        variant="default"
      >
        <Stack className="w-full" direction="col" gap={6}>
          {/* Step Header */}
          <Stack align="center" className="w-full" direction="row" gap={3}>
            <div
              className={cn(
                "flex items-center justify-center w-16 h-16 rounded-tracq shadow-tracq-card bg-gradient-to-br flex-shrink-0",
                getStepColor(),
              )}
            >
              {getStepIcon()}
            </div>
            <Stack className="flex-1 min-w-0" direction="col" gap={1}>
              <Heading className="text-[#F1F5F9] font-bold" level="h3" size="lg">
                {STEP_NAMES[step] || step}
              </Heading>
              <p className="text-[#94A3B8] text-sm">
                {STEP_DESCRIPTIONS[step] || "Complete this step to continue"}
              </p>
            </Stack>
            <Badge
              className={cn(
                "flex-shrink-0",
                status === "completed" && "bg-green-500/20 text-green-400 border-green-500/30",
                status === "active" &&
                  "bg-tracq-bright-blue/20 text-tracq-bright-blue border-tracq-bright-blue/30",
              )}
              size="sm"
              variant={getBadgeVariant(status)}
            >
              {getBadgeLabel(status)}
            </Badge>
          </Stack>

          {/* Step Content */}
          <div className="bg-[#0F172A] rounded-tracq p-6 border border-[#334155] w-full">
            {renderStepContent()}
          </div>

          {/* Action Buttons - Primary Action Prominent */}
          <Stack
            className="justify-between items-center pt-4 border-t border-[#334155]"
            direction="row"
            gap={3}
          >
            <Button
              className="border-[#334155] text-[#94A3B8] hover:bg-[#334155] hover:border-tracq-bright-blue/50 hover:text-tracq-bright-blue"
              disabled={isLoading}
              onClick={handleSkip}
              size="md"
              variant="outline"
            >
              <SkipForward className="w-4 h-4 mr-2" />
              Skip Step
            </Button>
            <Button
              className="bg-gradient-to-r from-tracq-deep-blue to-tracq-bright-blue hover:from-tracq-deep-blue/90 hover:to-tracq-bright-blue/90 text-white font-semibold px-8 shadow-tracq-card"
              disabled={isLoading}
              onClick={handleComplete}
              size="lg"
              variant="primary"
            >
              <CheckCircle className="w-5 h-5 mr-2" />
              {isLoading ? "Completing..." : "Complete Step"}
            </Button>
          </Stack>
        </Stack>
      </Card>
    );
  },
);

StepCard.displayName = "StepCard";
